import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';

import { Button, Modal, ModalDialogue } from '@sg/garnish';
import { OrderState } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderTerminalStateModal = (props: Props) => {
  const { hasCurrentUserItems, hostDisplayName, orderChannel, orderState, onDismiss } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const { headline, body } = getTerminalStateText({
    formatMessage,
    hasItems: String(hasCurrentUserItems),
    hostDisplayName,
    orderChannel,
    orderState,
  });

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      visible
      fitHeight
      size="medium"
      withCloseBtn={false}
      accessibilityLabel={headline}
      headline={headline}
      body={body}
      onRequestClose={onDismiss}
    >
      <Modal.Footer withoutTopBorder>
        <Button palette="primary" size="large" onPress={onDismiss}>
          <FormattedMessage {...messages.gotIt} />
        </Button>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTerminalStateText(params: Params) {
  const { formatMessage, hasItems, hostDisplayName, orderChannel, orderState } = params;

  const isCompleted = orderState === OrderState.Complete;

  if (isCompleted && hostDisplayName) {
    return {
      headline: formatMessage(messages.completedHeadline, { host_display_name: hostDisplayName }),
      body: formatMessage(messages.completedBody, {
        has_items: hasItems,
        host_display_name: hostDisplayName,
        order_channel: orderChannel,
      }),
    };
  }

  if (isCompleted) {
    return {
      headline: formatMessage(messages.completedHeadlineWithoutHost),
      body: formatMessage(messages.completedBodyWithoutHost, {
        has_items: hasItems,
        order_channel: orderChannel,
      }),
    };
  }

  if (hostDisplayName) {
    return {
      headline: formatMessage(messages.canceledHeadline, { host_display_name: hostDisplayName }),
      body: formatMessage(messages.canceledBody),
    };
  }

  return {
    headline: formatMessage(messages.canceledHeadlineWithoutHost),
    body: formatMessage(messages.canceledBody),
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly completedHeadline: { readonly host_display_name: MessageValue };
  readonly completedBody: {
    readonly has_items: string;
    readonly host_display_name: MessageValue;
    readonly order_channel: string;
  };
  readonly canceledHeadline: { readonly host_display_name: MessageValue };
  readonly canceledBody: NoMessageValues;
  readonly completedHeadlineWithoutHost: NoMessageValues;
  readonly completedBodyWithoutHost: { readonly has_items: string; readonly order_channel: string };
  readonly canceledHeadlineWithoutHost: NoMessageValues;
  readonly gotIt: NoMessageValues;
}>({
  completedHeadline: {
    defaultMessage: '{host_display_name} submitted the group order',
    description: 'Group Order | Completed modal headline',
  },
  completedBody: {
    defaultMessage:
      '{has_items, select, true {Your items were included. } other {}}Reach out to {host_display_name} for {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} details.',
    description: 'Group Order | Completed modal body',
  },
  canceledHeadline: {
    defaultMessage: "{host_display_name}'s group order has ended",
    description: 'Group Order | Canceled modal headline',
  },
  canceledBody: {
    defaultMessage: 'Your items were removed. Nothing was charged to you.',
    description: 'Group Order | Canceled modal body',
  },
  completedHeadlineWithoutHost: {
    defaultMessage: 'The group order was submitted',
    description: 'Group Order | Completed modal headline without a host name',
  },
  completedBodyWithoutHost: {
    defaultMessage:
      '{has_items, select, true {Your items were included. } other {}}Reach out to the host for {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} details.',
    description: 'Group Order | Completed modal body without a host name',
  },
  canceledHeadlineWithoutHost: {
    defaultMessage: 'This group order has ended',
    description: 'Group Order | Canceled modal headline without a host name',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description: 'Group Order | Terminal state modal CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  hasCurrentUserItems: boolean;
  hostDisplayName: string | undefined;
  orderChannel: OrderChannel;
  orderState: OrderState.Complete | OrderState.Discarded;
  onDismiss: () => void;
};

type Params = {
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  hasItems: string;
  hostDisplayName: string | undefined;
  orderChannel: OrderChannel;
  orderState: OrderState.Complete | OrderState.Discarded;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
